import { put, takeEvery, takeLatest, call } from 'redux-saga/effects';
import { serializeKeys, deserializeKeys } from '@iso/lib/helpers/keyNormalizer';
import request from '@iso/lib/helpers/httpClient';
import * as CONSTANTS from './constants';
import * as ACTIONS from './actions';
import {
  fetchFreelancerJobCounterFail,
  fetchFreelancerJobCounterSuccess
} from './actions';

export function* acceptOrDeclineJob(action) {
  try {
    const { id, payload } = action;
    const data = yield call(
      request,
      `/job_memo/${id}/accept_decline/`,
      'POST',
      deserializeKeys(payload),
      true
    );
    action.callBack();
    yield put(
      ACTIONS.acceptOrDeclineJobSuccess(
        serializeKeys(data?.job_manager || null)
      )
    );
  } catch (error) {
    action.onError(error);
    yield put(ACTIONS.acceptOrDeclineJobFail(error));
  }
}

export function* cancelJob(action) {
  try {
    const { id, payload } = action;
    yield call(
      request,
      `/job_memo/${id}/cancel_booked_memo/`,
      'POST',
      deserializeKeys(payload),
      true
    );
    yield put(ACTIONS.cancelJobSuccess(serializeKeys({ id, ...payload })));
  } catch (error) {
    yield put(ACTIONS.cancelJobFail(error));
  }
}

export function* fetchJobs({ contractorId, filter }) {
  try {
    const data = yield call(
      request,
      `/user/${contractorId}/job_memos/`,
      'GET',
      filter
    );
    yield put(ACTIONS.fetchContractorJobsSuccess(serializeKeys(data)));
  } catch (error) {
    yield put(ACTIONS.fetchContractorJobsFail(error));
  }
}

export function* fetchJobDetail({ id }) {
  try {
    const data = yield call(request, `/job_memo/${id}/detail/`, 'GET');
    yield put(ACTIONS.fetchContractorJobDetailSuccess(serializeKeys(data)));
  } catch (error) {
    yield put(ACTIONS.fetchContractorJobDetailFail(error));
  }
}

export function* updateJob({
  jobId,
  payload,
  callBack = () => {},
  onError = () => {}
}) {
  try {
    const data = yield call(
      request,
      `/job_memo/${jobId}/update_hold/`,
      'POST',
      deserializeKeys(payload)
    );
    callBack();
    yield put(ACTIONS.updateContractorJobSuccess(serializeKeys(data)));
  } catch (error) {
    onError(error);
    yield put(ACTIONS.updateContractorJobFail(error));
  }
}

export function* deleteBulkMemo({
  jobIds,
  callBack = () => {},
  onError = () => {}
}) {
  try {
    yield call(
      request,
      `/job_memo/bulk_delete/`,
      'POST',
      deserializeKeys({ ids: jobIds })
    );
    callBack();
    yield put(ACTIONS.deleteBulkContractorJobSuccess(serializeKeys(jobIds)));
  } catch (error) {
    onError(error);
    yield put(ACTIONS.deleteBulkContractorJobFail(error));
  }
}

export function* archiveMemo({ memoId, callBack, onError }) {
  try {
    const data = yield call(
      request,
      `/job_memo/${memoId}/archive/`,
      'POST',
      deserializeKeys({})
    );
    callBack();
    yield put(ACTIONS.archiveMemoSuccess(serializeKeys(data)));
  } catch (error) {
    onError(error);
    yield put(ACTIONS.archiveMemoFail(error));
  }
}

export function* fetchArchivedMemos({ contractorId }) {
  try {
    const rawData = yield call(
      request,
      `/user/${contractorId}/job_memos/archived/`,
      'GET'
    );
    const data = rawData.map((memo) =>
      serializeKeys({ ...memo, key: memo.id })
    );
    yield put(ACTIONS.fetchArchivedMemoSuccess(data));
  } catch (error) {
    yield put(ACTIONS.fetchArchivedMemoFail(error));
  }
}

export function* deleteBulkArchivedMemos({
  jobIds,
  callBack = () => {},
  onError = () => {}
}) {
  try {
    yield call(
      request,
      `/job_memo/bulk_delete/`,
      'POST',
      deserializeKeys({ ids: jobIds })
    );
    callBack();
    yield put(ACTIONS.deleteBulkArchivedMemoSuccess(serializeKeys(jobIds)));
  } catch (error) {
    onError(error);
    yield put(ACTIONS.deleteBulkArchivedMemoFail(error));
  }
}

export function* fetchDeclinedMemos({ contractorId }) {
  try {
    const rawData = yield call(
      request,
      `/user/${contractorId}/job_memos/declined/`,
      'GET'
    );
    const data = rawData.map((memo) =>
      serializeKeys({ ...memo, key: memo.id })
    );
    yield put(ACTIONS.fetchDeclinedMemoSuccess(data));
  } catch (error) {
    yield put(ACTIONS.fetchDeclinedMemoFail(error));
  }
}

export function* deleteBulkDeclinedMemos({
  jobIds,
  callBack = () => {},
  onError = () => {}
}) {
  try {
    yield call(
      request,
      `/job_memo/bulk_delete/`,
      'POST',
      deserializeKeys({ ids: jobIds })
    );
    callBack();
    yield put(ACTIONS.deleteBulkDeclinedMemoSuccess(serializeKeys(jobIds)));
  } catch (error) {
    onError(error);
    yield put(ACTIONS.deleteBulkDeclinedMemoFail(error));
  }
}

export function* deleteArchivedMemos({ jobMemoId }) {
  try {
    yield call(request, `/job_memo/${jobMemoId}/?mode=hide`, 'DELETE');
    yield put(ACTIONS.deleteJobSuccess({ jobMemoId }));
  } catch (error) {
    yield put(ACTIONS.deleteJobFail(error));
  }
}

export function* getJobCounter() {
  try {
    const data = yield call(
      request,
      `/auth/new_jobs_counter`,
      'GET'
    );
    yield put(ACTIONS.fetchFreelancerJobCounterSuccess(data));
  } catch (error) {
    yield put(ACTIONS.fetchFreelancerJobCounterFail(error));
  }
}

export default function* contractorJobSaga() {
  yield takeEvery(CONSTANTS.ACCEPT_OR_DECLINE_JOB_REQUEST, acceptOrDeclineJob);
  yield takeEvery(CONSTANTS.CANCEL_JOB_REQUEST, cancelJob);
  yield takeLatest(CONSTANTS.FETCH_CONTRACTOR_JOBS_REQUEST, fetchJobs);
  yield takeLatest(
    CONSTANTS.FETCH_CONTRACTOR_JOB_DETAIL_REQUEST,
    fetchJobDetail
  );
  yield takeEvery(CONSTANTS.UPDATE_CONTRACTOR_JOB_REQUEST, updateJob);
  yield takeEvery(CONSTANTS.DELETE_BULK_CONTRACTOR_JOB_REQUEST, deleteBulkMemo);
  yield takeEvery(CONSTANTS.ARCHIVE_MEMO_REQUEST, archiveMemo);
  yield takeEvery(CONSTANTS.FETCH_ARCHIVED_MEMO_REQUEST, fetchArchivedMemos);
  yield takeEvery(
    CONSTANTS.DELETE_BULK_ARCHIVED_MEMO_REQUEST,
    deleteBulkArchivedMemos
  );
  yield takeEvery(CONSTANTS.FETCH_DECLINED_MEMO_REQUEST, fetchDeclinedMemos);
  yield takeEvery(
    CONSTANTS.DELETE_BULK_DECLINED_MEMO_REQUEST,
    deleteBulkDeclinedMemos
  );
  yield takeEvery(
    CONSTANTS.DELETE_SINGLE_JOB_MEMO_REQUEST,
    deleteArchivedMemos
  );

  yield takeEvery(CONSTANTS.FETCH_JOB_COUNTER_REQUEST, getJobCounter);
}
